/**
 * PhoneAdminConsoleCard — per-customer settings for the phone admin
 * IVR console (in-flight feature, see docs/backlog.md).
 *
 *   * audit_retention_days — retention window for admin_audit_log
 *     entries scoped to this customer. CHECK constraint allows
 *     30 / 60 / 90; default 90. Same shape as the recycle-bin slider.
 *
 * NOTE: a `main_tree_id` field exists in the schema (migration 0073)
 * but the UI for it was removed at Sam's request 2026-05-07 — picking
 * a tree from the spoken picker is fast enough that defaulting to a
 * "main" tree adds no value. The column is kept harmless in the DB so
 * we can revisit if usage ever changes; the dial-in flow no longer
 * reads it (always shows the full picker on multi-tree accounts).
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { customers } from '@/api/customers';
import type { CustomerRow } from '@/api/customers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const RETENTION_CHOICES: ReadonlyArray<30 | 60 | 90> = [30, 60, 90];

export function PhoneAdminConsoleCard({
  customer,
}: { customer: CustomerRow }): JSX.Element {
  const qc = useQueryClient();
  const [retentionDays, setRetentionDays] = useState<30 | 60 | 90>(
    customer.audit_retention_days ?? 90,
  );
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const save = useMutation({
    mutationFn: () => customers.update(customer.id, {
      auditRetentionDays: retentionDays,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer', customer.id] });
      setSavedAt(Date.now());
    },
  });

  const dirty = retentionDays !== (customer.audit_retention_days ?? 90);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Phone admin IVR console</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Settings for admins who dial the reseller&apos;s admin number to manage
          this account&apos;s IVR over the phone.
        </p>
      </CardHeader>
      <CardContent className="space-y-5 text-sm">
        <fieldset className="space-y-2">
          <legend className="font-medium">Audit log retention</legend>
          <p className="text-xs text-muted-foreground">
            How long admin actions stay in the audit log. Older entries are
            purged daily.
          </p>
          <div className="flex gap-2">
            {RETENTION_CHOICES.map((days) => (
              <Button
                key={days}
                type="button"
                size="sm"
                variant={retentionDays === days ? 'default' : 'outline'}
                onClick={() => setRetentionDays(days)}
                aria-pressed={retentionDays === days}
              >
                {days} days
              </Button>
            ))}
          </div>
        </fieldset>

        <div className="flex items-center gap-3 pt-1">
          <Button
            type="button"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {savedAt !== null && !dirty && (
            <span className="text-xs text-muted-foreground" role="status">
              Saved.
            </span>
          )}
          {save.isError && (
            <span role="alert" className="text-xs text-destructive">
              {save.error instanceof Error ? save.error.message : 'Save failed'}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
